// src/pages/functions/FunctionSystemControlDetailPage.js
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import ProductGrid from "../../components/category_page/ProductGrid";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

export default function FunctionSystemControlDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const goToFilteredProducts = () =>
    navigate("/kategoriak?all=1&functions=rendszer-vezerles");
  const goToBuilder = () =>
    navigate("/builder", { state: { functionIds: ["rendszer-vezerles"] } });

  return (
    <motion.div
      key={location.key}
      className="min-h-screen bg-gradient-to-b from-white to-slate-50 text-[#0c1a26]"
      initial="hidden"
      animate="show"
    >
      {/* ===== HERO – egységes stílus (Home/Builder) ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c1a26] to-[#173047]" />
        <div className="absolute inset-0 opacity-25">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${"/img/t5.webp"})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-20 md:pb-28 text-white">
          <motion.h1 className="text-3xl md:text-5xl font-bold drop-shadow-sm" variants={fadeUp}>
            Rendszervezérlés
          </motion.h1>
          <motion.p className="text-white/90 text-lg md:text-xl max-w-3xl mt-2" variants={fadeUp}>
            Minden funkció <strong>egy mozdulatra</strong>: jelenetek, master gombok, fali panelek és
            személyre szabott dashboardok – hogy az otthonod mindig úgy reagáljon, ahogy te szeretnéd.
          </motion.p>

          <motion.div className="mt-7 flex flex-wrap gap-3" variants={fadeUp}>
            <button
              onClick={goToFilteredProducts}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-700 px-5 py-3 font-semibold"
            >
              Termékek (előszűrve) →
            </button>
            <button
              onClick={goToBuilder}
              className="inline-flex items-center justify-center rounded-xl border border-white/40 hover:bg-white hover:text-[#0c1a26] px-5 py-3 font-semibold"
            >
              Kezd el a Builderben
            </button>
            <button
              onClick={() => navigate("/funkciok")}
              className="inline-flex items-center justify-center rounded-xl border border-white/40/60 hover:bg-white/10 px-5 py-3 font-semibold"
            >
              Vissza a funkciókhoz
            </button>
          </motion.div>
        </div>
      </section>

      {/* ===== TARTALOM ===== */}
      <div className="max-w-7xl mx-auto px-6 py-10 space-y-14">

        {/* Bevezető + fő kép (a korábbi oldal mintájára) */}
        <section className="grid md:grid-cols-[1.3fr_1fr] gap-6 items-stretch">
          <motion.div className="bg-white/90 backdrop-blur rounded-2xl border p-5 md:p-6" variants={fadeUp}>
            <h2 className="text-2xl font-semibold">Mi ez?</h2>
            <p className="text-gray-700 mt-2">
              A rendszervezérlés célja, hogy <strong>kevesebb mikrokezeléssel</strong> kapj
              következetes élményt: a jelenetek egy érintésre mindent a helyére tesznek –
              fény, árnyékolás, klíma, zene, biztonság.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Master gombok</div>
                <div className="text-sm text-gray-700">„Távozom”, „Érkezem”, „Mozi” – egy érintéssel.</div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Fali panelek / dashboard</div>
                <div className="text-sm text-gray-700">Átlátható, családbarát vezérlés tableten is.</div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Gyors jelenetek</div>
                <div className="text-sm text-gray-700">Fény, klíma, zene és árnyékolás együtt változik.</div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Integráció</div>
                <div className="text-sm text-gray-700">Ökoszisztémák és eszközök összehangolása.</div>
              </div>
            </div>
          </motion.div>

          {/* FŐ KÉP – /img/base/rendszer-vezerles.webp */}
          <motion.figure className="rounded-2xl border overflow-hidden bg-white" variants={fadeUp}>
            <img
              src="/img/base/rendszer-vezerles.webp"
              alt="Rendszervezérlés – fő illusztráció"
              className="w-full h-full object-contain"
              loading="lazy"
            />
            <figcaption className="px-4 py-2 text-xs text-gray-600">
              Rendszervezérlés – fali panelek, jelenetgombok, dashboardok.
            </figcaption>
          </motion.figure>
        </section>

        {/* Videók – két rövid MP4, loop + leírás */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-3" variants={fadeUp}>
            Hogyan néz ki működés közben?
          </motion.h2>
          <p className="text-sm text-gray-600 mb-4">
            Rövid demók: „jelenetek, amik működnek” és „master gombok nagy hatással” – a videók halkan, végtelenítve futnak.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Video #1 */}
            <motion.figure className="rounded-2xl overflow-hidden border bg-black" variants={fadeUp}>
              <video
                className="w-full h-auto"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source src="/videos/rendszervezerles-1.mp4" type="video/mp4" />
              </video>
              <figcaption className="p-3 bg-white text-sm text-gray-700">
                <strong>Jelenetek, amik működnek:</strong> egy érintés, és a fények, redőnyök, klíma és zene
                <em> összehangoltan</em> változnak.
              </figcaption>
            </motion.figure>

            {/* Video #2 */}
            <motion.figure className="rounded-2xl overflow-hidden border bg-black" variants={fadeUp}>
              <video
                className="w-full h-auto"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source src="/videos/rendszervezerles-2.mp4" type="video/mp4" />
              </video>
              <figcaption className="p-3 bg-white text-sm text-gray-700">
                <strong>Master gombok, nagy hatás:</strong> „Távozom” – minden le, zár és élesít;
                „Érkezem” – otthonos fény és komfort <em> azonnal</em>.
              </figcaption>
            </motion.figure>
          </div>
        </section>

        {/* Mikor használd? */}
        <section className="grid md:grid-cols-2 gap-6">
          <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
            <h3 className="font-semibold mb-2">Mindennapi rutin</h3>
            <p className="text-gray-700 text-sm">Reggeli indulás, esti leállás, hétvégi mód – egy gombbal.</p>
          </motion.div>
          <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
            <h3 className="font-semibold mb-2">Családi használat</h3>
            <p className="text-gray-700 text-sm">Egyszerű, érthető kezelők a gyerekeknek és a vendégeknek.</p>
          </motion.div>
          <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
            <h3 className="font-semibold mb-2">Komplex terek</h3>
            <p className="text-gray-700 text-sm">Több körös világítás, redőnyök, zene – összehangolva, könnyen.</p>
          </motion.div>
          <motion.div className="bg-white border rounded-xl p-4 shadow-sm" variants={fadeUp}>
            <h3 className="font-semibold mb-2">Távollét/nyaralás</h3>
            <p className="text-gray-700 text-sm">Jelenetek időzítve, távolról felülírhatók, naplózva.</p>
          </motion.div>
        </section>

        {/* Előnyök és korlátok */}
        <section className="grid md:grid-cols-2 gap-6">
          <motion.div className="bg-green-50 border border-green-200 rounded-xl p-4" variants={fadeUp}>
            <h3 className="font-semibold mb-2">Előnyök</h3>
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
              <li>Villámgyors vezérlés, kevesebb app-böngészés</li>
              <li>Jelenet-alapú, következetes működés</li>
              <li>Központi és helyi kezelők együtt</li>
            </ul>
          </motion.div>
          <motion.div className="bg-red-50 border border-red-200 rounded-xl p-4" variants={fadeUp}>
            <h3 className="font-semibold mb-2">Korlátok</h3>
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
              <li>Jó jelenetekhez kezdeti tervezés kell</li>
              <li>Fali panelek kompatibilitása gyártófüggő lehet</li>
              <li>Komplexebb házaknál szakértői beállítás javasolt</li>
            </ul>
          </motion.div>
        </section>

        {/* Ajánlott termékek – funkció szerint előszűrve (ProductGrid) */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-4" variants={fadeUp}>
            Ajánlott termékek
          </motion.h2>
          <div className="rounded-2xl border bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm">
            <ProductGrid
              activeFilters={{ functionId: "rendszer-vezerles" }}
              setActiveFilters={() => {}}
              categoriesMap={{}}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={goToFilteredProducts}
              className="rounded-xl bg-blue-600 text-white px-5 py-3 hover:bg-blue-700"
            >
              Termékek teljes listája →
            </button>
            <button
              onClick={goToBuilder}
              className="rounded-xl border px-5 py-3 hover:bg-gray-50"
            >
              Rendszervezérlés építése a Builderben
            </button>
          </div>
        </section>
      </div>

      {/* Sticky mobil CTA */}
      <div className="md:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
        <motion.button
          className="bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg"
          onClick={goToFilteredProducts}
          aria-label="Ugrás a rendszervezérlés termékekhez"
          whileTap={{ scale: 0.98 }}
          whileHover={{ scale: 1.02 }}
        >
          Rendszervezérlés termékek →
        </motion.button>
      </div>
    </motion.div>
  );
}
